import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { saveAs } from 'file-saver';
import {
  RightArrowIcon,
  toSentancePascalCase,
  UserDetails,
  DeviceTypes,
  useDeviceType,
  getDecryptedValue,
  setEncryptedValue,
  CommonErrorMessage,
  convertStringToDate,
  convertToDate,
  getCache,
  deleteEncryptedValue,
  Proxy,
  getLocalStorageBoolean,
  backToDashboardLink,
  apiResponseSafeCheck,
  NavigateConst,
} from 'common';
import {
  useMyDepedentAccess,
  useMemberIDCard,
  useMedicalClaimList,
  useGetDependentAccess,
  useMedicalClaimEOB,
} from '@sentara-health/sentara-api-hooks-core';
import { YearDropdown } from 'templates';
import { GlobalContext, isAvMed } from 'context';
import { DependentConsentModals } from '../authorization/DependentConsentModals';
import { useDependentConsent } from '../authorization/useDependentConsent';
import DependentAccessCheckbox from '../authorization/DependentAccessCheckbox';
import {
  Button,
  Headings,
  Label,
  Checkbox,
  Accordion,
  ContactInfo,
  Loader,
  Link,
  Table,
  Paragraph,
  ExternalLinkIcon,
  DatePicker,
  SortArrowIcon,
} from '@sentara-health/optimahealth_web';
import { useForm, FormProvider } from 'react-hook-form';

const MedicalClaims: React.FC<any> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const year: number = new Date().getFullYear();
  const [membersTable, setMembersTable] = useState<any>([]);
  const [medicalClaimsTable, setMedicalClaimsTable] = useState<any>([]);
  const { loginMID, serviceNumber, primaryUser, getFeature, ssoEndPoints } =
    useContext(GlobalContext);
  const consent = useDependentConsent({
    checkMemberAccess,
    membersTable,
  });
  const {
    dependentAccess,
    setDependentAccess,
    handleDependentAccessChange,
    openViewTermsModal,
    openErrorModal,
  } = consent;
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [getPhone, setPhone] = useState<any>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMedical, setShowMedical] = useState(false);
  const [showSpouse, setShowSpouse] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPages, setMaxPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [memberId, setMemberId] = useState<any>(null);
  const [invalidDateFormat, setInvalidDateFormat] = useState(false);
  const [emptyDate, setEmptyDate] = useState(false);
  const [invalidDateRange, setInvalidDateRange] = useState(false);
  const [showParent, setShowParent] = useState(false);
  const { memberIDCardType } = useMemberIDCard();
  const { medicalClaimListType } = useMedicalClaimList();
  const { GetDependentAccessType } = useGetDependentAccess();
  const { MyDepedentAccessType } = useMyDepedentAccess();
  const { MedicalClaimEOBType } = useMedicalClaimEOB();
  const [loader, setLoader] = useState(false);
  const [sortField, setSortField] = useState('');
  const [yearValue, setYearValue] = useState<any>(year);
  const [order, setOrder] = useState('asc');
  const location = useLocation();
  const pageLimit = 10;
  const behaviourlClaims = 'behavioral-claims';
  const medicalClaims = 'medical-claims';
  const methods = useForm({
    mode: 'onChange',
  });
  const { handleSubmit, watch, setValue, getValues } = methods;

  const watchedFrom = watch('from');
  const watchedTo = watch('to');

  useEffect(() => {
    setFromDate(getValues('from'));
    setToDate(getValues('to'));
  }, [watchedFrom, watchedTo, getValues]);

  // Function to handle sorting change
  const handleSortingChange = (accessor: string) => {
    const sortOrder =
      accessor === sortField && order === 'asc' ? 'desc' : 'asc';
    setSortField(accessor);
    setOrder(sortOrder);
    handleSorting(accessor, sortOrder);
  };

  // Function to handle sorting
  const handleSorting = (sortField: string, sortOrder: string) => {
    if (sortField) {
      const sorted: Array<any> = medicalClaimsTable?.predectiveSearches?.sort(
        (a: any, b: any) => {
          if (a[sortField] === null) {
            return 1;
          }
          if (b[sortField] === null) {
            return -1;
          }
          if (a[sortField] === null && b[sortField] === null) {
            return 0;
          }
          return (
            String(a[sortField]).localeCompare(String(b[sortField]), 'en', {
              numeric: true,
            }) * (sortOrder === 'asc' ? 1 : -1)
          );
        }
      );
      setMedicalClaimsTable({
        predectiveSearches: sorted,
        totalcount: medicalClaimsTable?.totalcount,
      });
    }
  };

  //Fetching membersData List
  async function membersData() {
    // Show the loader while the members API call is in progress.
    setLoader(true);
    try {
      const result = await memberIDCardType('claims');
      if (result?.data?.members) {
        setMembersTable(result?.data.members);

        //To display the Allow parent/spouse text
        if (result?.data.parentPermission === true) {
          setShowParent(true);
        } else if (result?.data.spoucePermission === true) {
          setShowSpouse(true);
        }
      } else if (result?.errorCode) {
        // API returned an error code: stop the loader and show the error modal.
        // openErrorModal routes 401/440 to logout and displays the modal for other codes.
        setLoader(false);
        openErrorModal(result);
      }
    } catch (error) {
      // Unexpected/thrown error: stop the loader and surface it via the error modal.
      setLoader(false);
      openErrorModal(error);
    }

    setPhone(JSON.parse(serviceNumber || '{}'));
  }

  // Fetching mediacal Claim List API
  const dependentDetails = async (mid?: string, resetPagination?: number) => {
    setLoader(true);
    setDateData(mid);
    const offSet = searchParams.get('offset')
      ? Number(searchParams.get('offset'))
      : currentPage;
    const currentPageNumber = resetPagination ?? offSet;
    setCurrentPage(currentPageNumber);

    let userMemberId = mid ?? memberId;
    const result = await medicalClaimListType(
      {
        startDate: searchParams.get('startDate')
          ? searchParams.get('startDate') ?? fromDate
          : fromDate,
        endDate: searchParams.get('toDate')
          ? searchParams.get('toDate') ?? toDate
          : toDate,
        memberId: searchParams.get('memberId')
          ? searchParams.get('memberId')
          : userMemberId,
        claimtype: showMedical ? '0' : '1',
        offset: currentPageNumber,
        limit: 10,
      },
      getCache()
    );
    dependentDetailsData(result);
  };
  const setDateData = (mid?: string) => {
    setMemberId(
      searchParams.get('memberId')
        ? searchParams.get('memberId')
        : mid ?? memberId
    );
    setFromDate(
      searchParams.get('startDate')
        ? searchParams.get('startDate') ?? ''
        : fromDate
    );
    setToDate(
      searchParams.get('endDate') ? searchParams.get('endDate') ?? '' : toDate
    );
  };

  const dependentDetailsData = (result: any) => {
    if (result?.data) {
      setLoader(false);

      setMedicalClaimsTable(result?.data);
      setSearchParams({});
    } else if (result?.error || result?.data !== undefined) {
      setMedicalClaimsTable(result?.data);
      setLoader(false);
      setTotalItems(medicalClaimsTable?.totalcount);
      erroHandling(result?.error);
    }
  };

  //Error handling for 401 || 440 error code, naviagte user to logout
  const erroHandling = (error: any) => {
    if (
      error === 'Request failed with status code 401' ||
      error === 'Request failed with status code 440'
    ) {
      localStorage.clear();
      deleteEncryptedValue();
      window.location.href = NavigateConst.signIn;
    }
  };

  // Date validation logic
  const onSubmit = (data: any) => {
    setInvalidDateFormat(false);
    setEmptyDate(false);
    setInvalidDateRange(false);
    const fromDateLength = data?.from?.length > 0 && data?.from?.length < 10;

    const toDateLength = data?.to?.length > 0 && data?.to?.length < 10;
    const formattedFromDate = convertStringToDate(data?.from);
    const formattedToDate = convertStringToDate(data?.to);
    const isFromDateValid = isValidDateFormat(data?.from);
    const isToDateValid = isValidDateFormat(data?.to);

    if (
      fromDateLength ||
      toDateLength ||
      !isFromDateValid ||
      !isToDateValid ||
      formattedFromDate === 'Invalid date' ||
      formattedToDate === 'Invalid date'
    ) {
      setInvalidDateFormat(true);
      setEmptyDate(false);
      setInvalidDateRange(false);
    } else if (data?.from === '' || data?.to === '') {
      setEmptyDate(true);
      setInvalidDateFormat(false);
      setInvalidDateRange(false);
    } else if (
      new Date(data?.from) > new Date(data?.to) ||
      new Date() < new Date(data?.to)
    ) {
      setInvalidDateRange(true);
      setEmptyDate(false);
      setInvalidDateFormat(false);
    } else {
      dependentDetails(undefined, 1);
    }
  };

  // Generate PDF for EOB based on claim data
  const eobPDF = async (data: any) => {
    setLoader(true);
    const postData = {
      claimID: data,
      claimType: showMedical ? '0' : '1',
      memberId: memberId,
    };
    const response = await MedicalClaimEOBType(postData, getCache());
    if (response?.data !== undefined) {
      pdfConverter(response, data);
    } else if (response?.error) {
      setLoader(false);
    }
  };

  // Function to generate PDF from data
  const pdfConverter = (data: any, fileName: any) => {
    const filename = fileName;
    const binary = atob(data && data?.data?.replace(/([^\r])\n/g, '$1\r\n'));
    const len = binary.length;
    const buffer = new ArrayBuffer(len);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < len; i++) {
      view[i] = binary.charCodeAt(i);
    } // create the blob object with content-type "application/pdf"
    const blob = new Blob([view], { type: 'application/pdf' });
    saveAs(blob, filename);
    setLoader(false);
  };

  useEffect(() => {
    (async () => {
      if (location.pathname.endsWith(medicalClaims)) {
        setShowMedical(true);
      } else if (location.pathname.endsWith(behaviourlClaims)) {
        setShowMedical(false);
      }
      setDefaultDate();
      setInvalidDateFormat(false);
      setInvalidDateRange(false);
      setEmptyDate(false);
      await membersData();
      const response = await GetDependentAccessType();
      setDependentAccess(response?.data?.isDependentAccess || false);
    })();
  }, [location.pathname]);

  const setDefaultDate = () => {
    const currentDate = new Date();
    currentDate.setMonth(currentDate.getMonth() - 18);
    const fromdate = convertToDate(currentDate);
    const todate = convertToDate(new Date());
    setToDate(todate);
    setFromDate(fromdate);
    setInvalidDateFormat(false);
    setEmptyDate(false);
    setInvalidDateRange(false);
    setValue('from', fromdate);
    setValue('to', todate);
  };

  const setMemberDetails = async (MID: string) => {
    setMemberId(MID);
    setEncryptedValue(UserDetails.memberIdData, MID);
  };

  useEffect(() => {
    setDefaultDate();
    if (!getLocalStorageBoolean(backToDashboardLink)) {
      setMemberDetails(loginMID);
    }
  }, [loginMID]);

  useEffect(() => {
    (async () => {
      if (membersTable?.length > 0) {
        if (getLocalStorageBoolean(backToDashboardLink)) {
          let ProxyId = await getDecryptedValue(Proxy.memberProxy);
          ProxyId = ProxyId ? JSON.parse(ProxyId) : '';
          const decryptedValue = ProxyId.MemberId;
          let filteredMid = membersTable.filter(
            (item: { memberId: string }) => item.memberId === decryptedValue
          );
          const MID = filteredMid[0]?.mId;
          if (MID) {
            setMemberDetails(MID);
            dependentDetails(MID);
          }
        } else {
          dependentDetails();
        }
      }
    })();
  }, [membersTable, currentPage, showMedical]);

  useEffect(() => {
    if (medicalClaimsTable?.predectiveSearches?.length > 0) {
      setTotalItems(medicalClaimsTable?.totalcount);
      setMaxPages(Math.ceil(medicalClaimsTable?.totalcount / pageLimit));
    } else {
      setTotalItems(medicalClaimsTable?.totalcount);
    }
  }, [medicalClaimsTable, medicalClaimsTable?.predectiveSearches?.length]);

  async function checkMemberAccess(value: boolean) {
    setLoader(true);
    const data = (await getDecryptedValue(UserDetails.usedId)) || '';
    await MyDepedentAccessType({
      allowAccess: value,
      userId: data,
    });
    setLoader(false);
  }

  const nextPage = (e: any) => {
    e.preventDefault();

    if (currentPage <= maxPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = (e: any) => {
    e.preventDefault();
    if (currentPage >= 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    if (location.pathname === '/home/medical-claims') {
      localStorage.removeItem('selectedOption');
    }
  });

  // Render error message as response
  function renderErrorMessage() {
    if (invalidDateFormat) {
      return (
        <div className="text-danger">
          {t('medicalClaims.invalidDateFormat')}
        </div>
      );
    } else if (emptyDate) {
      return <div className="text-danger">{t('medicalClaims.emptyDate')}</div>;
    } else if (invalidDateRange) {
      return (
        <div className="text-danger">{t('medicalClaims.invalidDateRange')}</div>
      );
    } else {
      return null;
    }
  }
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  return (
    <>
      {loader && (
        <div className="container-fluid" data-testid="loader">
          <Loader />
        </div>
      )}
      <div className="not-print">
        <div className="d-lg-flex d-md-flex not-print align-items-center">
          <div className="col-md-auto col-sm-12 col-12 flex-1 mb-3">
            <Headings
              level={1}
              text={
                showMedical
                  ? t('medicalClaims.medcialClaimTitle')
                  : t('medicalClaims.behavioralClaimsTitle')
              }
              variant="semiBold"
              dataTestId={
                showMedical ? 'medicalClaimsHeader' : 'behavioralClaimsHeading'
              }
            />
          </div>
          {showMedical && getFeature?.ViewTaxYearSummary && (
            <div className="col-xl-4 col-md-5 col-sm-12 col-12 mb-3">
              <YearDropdown
                setMemberValue={() => {}}
                yearValue={yearValue}
                setYearValue={setYearValue}
                fromMedical={true}
              />
            </div>
          )}
        </div>

        {showMedical && (
          <>
            {(showSpouse || showParent) && getFeature?.Allowspouseviewclaims ? (
              <div className="mb-4">
                <DependentAccessCheckbox
                  accessType={showSpouse ? 'spouse' : 'parent'}
                  label={
                    showSpouse
                      ? t('medicalClaims.allowAccessSpouse')
                      : t('medicalClaims.allowAccessParent')
                  }
                  dependentAccess={dependentAccess}
                  onAccessChange={handleDependentAccessChange}
                  onViewTerms={openViewTermsModal}
                  viewTermsLabel={t('common.viewTerms')}
                  viewTermsDataTestId="medicalClaimsViewTermsButton"
                  showViewTerms={getFeature?.CheckboxPopup === true}
                />
              </div>
            ) : null}
            {!errorMessage && membersTable?.length > 0 && (
              <div className="table-responsive overflow-auto">
                <Table
                  className="medical-claims-table mb-4"
                  headerChildren={
                    <>
                      <th scope="col" data-testid="selectOneTableHeading">
                        {t('common.selectOne')}
                      </th>
                      <th scope="col" data-testid="viewClaimsForTableHeading">
                        {t('medicalClaims.viewClaimsfor')}
                      </th>
                      <th scope="col" data-testid="memberIdTableHeading">
                        {t('common.memberId')}
                      </th>
                    </>
                  }
                  rowsChildren={
                    <>
                      {membersTable?.length > 0 &&
                        membersTable.map((item: any, i: any) => {
                          return [
                            <tr key={item.mId}>
                              <td>
                                <div className="d-flex justify-content-center">
                                  <Checkbox
                                    type="radio"
                                    id={`member${i + 1}`}
                                    dataTestId={`medicalClaimCheckbox${i + 1}`}
                                    name={'members'}
                                    value={item.mId}
                                    checked={memberId === item.mId}
                                    onChange={() => {
                                      setMemberId(item.mId);
                                      setEncryptedValue(
                                        'memberIdData',
                                        item.mId
                                      );
                                    }}
                                  />
                                </div>
                              </td>
                              <th
                                scope="col"
                                data-testid={`viewClaimFor${i + 1}`}
                              >
                                {toSentancePascalCase(item.memberName)}
                                <span>
                                  {primaryUser === 'P' && loginMID === item.mId
                                    ? ' (' + t('common.primary') + ')'
                                    : ''}
                                </span>
                              </th>
                              <td data-testid={`memberID${i + 1}`}>
                                {item.memberId}
                              </td>
                            </tr>,
                          ];
                        })}
                    </>
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
      <FormProvider {...methods}>
        <form
          data-testid="submit_form"
          onSubmit={handleSubmit(onSubmit)}
          autoComplete="off"
        >
          {/* DATE PICKEK SHOULD COME EHERE */}
          {!errorMessage && (
            <>
              <div>
                <div data-testid="serachDateHeader" className="mb-2">
                  <Paragraph text={t('medicalClaims.bydate')} variant="bold" />
                </div>
                {renderErrorMessage()}
                <div className="row d-flex align-items-center justify-content-start">
                  <div className="mb-4 col-md-5 col-12">
                    <Label
                      className="pb-1"
                      htmlFor={''}
                      dataTestId="selectDateFromLabel"
                    >
                      {t('medicalClaims.from')}
                    </Label>
                    <DatePicker
                      selectedValue={fromDate}
                      updateValue={(value: any) => setValue('from', value)}
                      placeholder={t('common.selectDate')}
                      maxDate={new Date()}
                      inputMode="numeric"
                      dataTestId="fromDatePicker"
                    />
                  </div>
                  <div className="mb-4 col-md-5 col-12">
                    <Label
                      className="pb-1"
                      htmlFor={''}
                      fieldName="to"
                      dataTestId="selectDateToLabel"
                    >
                      {t('medicalClaims.to')}
                    </Label>
                    <DatePicker
                      selectedValue={toDate}
                      updateValue={(value: any) => setValue('to', value)}
                      placeholder={t('common.selectDate')}
                      maxDate={new Date()}
                      inputMode="numeric"
                      dataTestId="toDatePicker"
                    />
                  </div>
                  <div className="mb-sm-0 mb-4 col-md-2 col-12">
                    <Button
                      onClick={(e: any) => {
                        e.preventDefault();
                        setDefaultDate();
                      }}
                      variant="secondary_btn"
                      className="btn-sm-height"
                      dataTestId="clearButton"
                    >
                      {t('medicalClaims.clear')}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-12 col-md-auto mb-3">
                  <Button
                    dataTestId="viewClaimButton"
                    onKeyDown={(e: any) => {
                      e.stopPropagation();
                    }}
                    type="submit"
                    variant="primary_btn"
                    className="btn-md-height"
                  >
                    {t('common.viewRecentClaims')}
                  </Button>
                </div>
                {getFeature?.PayYourSentaraBill ? (
                  <div className="col-12 col-md-auto mb-4">
                    <Button
                      variant="primary_btn"
                      dataTestId={'PaySentaraMedicalBill'}
                      onClick={(e: any) => {
                        e.preventDefault();
                        navigate(NavigateConst.paySentaraBillURL);
                      }}
                      className="btn-md-height"
                    >
                      {t('common.payYourSentaraBill')}
                    </Button>
                  </div>
                ) : null}
                {getFeature?.ClaimsStaticLink && (
                  <div className="col-12 col-md-auto mb-4">
                    <Link
                      className="text-decoration-underline small-link"
                      to={ssoEndPoints?.claimsLinkUrl}
                      data-testid="claimsLink"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('medicalClaims.claimsLink')}
                      <span className="ml-4">
                        <ExternalLinkIcon />
                      </span>
                    </Link>
                  </div>
                )}
              </div>
              <div data-testid="medicalExplanations" className="mb-4">
                <Paragraph
                  text={
                    <>
                      {showMedical
                        ? t('common.medicalClaimsExplanation')
                        : t('common.behavioral')}
                    </>
                  }
                  variant="bold"
                />
              </div>
            </>
          )}
        </form>
      </FormProvider>
      {!isMobile && !errorMessage && (
        <div className="table-responsive overflow-auto">
          <Table
            className="medical-claims-table mb-5 print"
            data-testid="medicalClaimsTable"
            id={'viewauthorizationDetails'}
            headerChildren={
              <>
                <th scope="col" data-testid="claimIdTab">
                  <Button
                    className="white-color bg-transparent border-0 p-0 m-0"
                    onClick={() => handleSortingChange('claimId')}
                    title={t('medicalClaims.id')}
                    iconRight={<SortArrowIcon />}
                  >
                    {t('medicalClaims.id')}
                  </Button>
                </th>
                <th scope="col" data-testid="serviceDateTab">
                  <Button
                    className="white-color bg-transparent border-0 p-0 m-0"
                    onClick={() => handleSortingChange('serviceDate')}
                    iconRight={<SortArrowIcon />}
                  >
                    {t('medicalClaims.serviceDate')}
                  </Button>
                </th>
                <th scope="col" data-testid="statusTab">
                  <Button
                    className="white-color bg-transparent border-0 p-0 m-0"
                    onClick={() => handleSortingChange('status')}
                    iconRight={<SortArrowIcon />}
                  >
                    {t('common.status')}
                  </Button>
                </th>
                <th scope="col" data-testid="providerNameTab">
                  <Button
                    className="white-color bg-transparent border-0 p-0 m-0"
                    onClick={() => handleSortingChange('providerName')}
                    iconRight={<SortArrowIcon />}
                  >
                    {t('medicalClaims.providerName')}
                  </Button>
                </th>
              </>
            }
            rowsChildren={
              <>
                {medicalClaimsTable &&
                  medicalClaimsTable.predectiveSearches?.length > 0 &&
                  medicalClaimsTable?.predectiveSearches?.map(
                    (item: any, i: any) => {
                      return [
                        <tr key={item.claimId}>
                          <td data-testid={`claimIdLink${i}`}>
                            <div className="d-flex justify-content-start ps-4">
                              <Button
                                variant="darkblue-color bg-transparent border-0 text-decoration-underline p-0"
                                onClick={() => {
                                  setSearchParams({
                                    startDate: fromDate,
                                    endDate: toDate,
                                    memberId: memberId,
                                    claimtype: showMedical ? '0' : '1',
                                    offset: String(currentPage),
                                    limit: '10',
                                  });
                                  navigate(NavigateConst.claimsSummary, {
                                    state: {
                                      memberId: memberId,
                                      ClaimType: showMedical ? '0' : '1',
                                      endDate: toDate,
                                      startDate: fromDate,
                                      ClaimId: item.claimId,
                                      isEOB: item.eob,
                                      currentPage,
                                    },
                                  });
                                }}
                                dataTestId={`claimIdLinkA${i}`}
                              >
                                {item.claimId}
                              </Button>
                            </div>
                          </td>

                          <td data-testid={`medicalClaimsServiceDate${i}`}>
                            {convertStringToDate(item.serviceDate)}
                          </td>
                          <td data-testid={`medicalClaimStatus${i}`}>
                            {item.status}
                          </td>
                          <td data-testid={`providerName${i}`}>
                            {item.providerName}
                          </td>
                        </tr>,
                      ];
                    }
                  )}
                {medicalClaimsTable?.length === 0 && (
                  <tr>
                    <td colSpan={5} className="no-results text-center">
                      {t('common.noResults')}
                    </td>
                  </tr>
                )}
              </>
            }
          />
        </div>
      )}

      {isMobile &&
        (medicalClaimsTable &&
        medicalClaimsTable.predectiveSearches?.length > 0 ? (
          <div className="accordion-table">
            <Accordion
              initialActiveIndex={0}
              id="medical_claims_table"
              items={
                medicalClaimsTable &&
                medicalClaimsTable.predectiveSearches?.length > 0 &&
                medicalClaimsTable?.predectiveSearches?.map(
                  (item: any, i: any) => ({
                    title: (
                      <div data-testid={`claimIdLink${i}`}>
                        <span className="font-sixteen">
                          {t('medicalClaims.id')}
                        </span>
                        <div>
                          <Button
                            className="darkblue-color bg-transparent border-0 text-decoration-underline p-0 font-sixteen"
                            onClick={() => {
                              setSearchParams({
                                startDate: fromDate,
                                endDate: toDate,
                                memberId: memberId,
                                claimtype: showMedical ? '0' : '1',
                                offset: String(currentPage),
                                limit: '10',
                              });
                              navigate(NavigateConst.claimsSummary, {
                                state: {
                                  memberId: memberId,
                                  ClaimType: showMedical ? '0' : '1',
                                  endDate: toDate,
                                  startDate: fromDate,
                                  isEOB: item.eob,
                                  ClaimId: item.claimId,
                                  currentPage,
                                },
                              });
                            }}
                            role="link"
                          >
                            {item.claimId}
                          </Button>
                        </div>
                      </div>
                    ),
                    id: 'medical_claims_table_data',
                    content: (
                      <div>
                        <div data-testid={`serviceDateTab${i}`}>
                          {t('medicalClaims.serviceDate')}
                        </div>
                        <div
                          data-testid={`medicalClaimsServiceDate${i}`}
                          className="mb-3"
                        >
                          {convertStringToDate(item.serviceDate)}
                        </div>
                        <div data-testid={`statusTab${i}`}>
                          {t('common.status')}
                        </div>
                        <div
                          data-testid={`medicalClaimStatus${i}`}
                          className="mb-3"
                        >
                          {item.status}
                        </div>
                        <div data-testid={`providerNameTab${i}`}>
                          {t('medicalClaims.providerName')}
                        </div>
                        <div data-testid={`providerName${i}`} className="mb-3">
                          {item.providerName}
                        </div>
                      </div>
                    ),
                  })
                )
              }
            />
          </div>
        ) : (
          medicalClaimsTable?.length === 0 && (
            <div className="mb-4">
              <div className="text-center">{t('common.noResults')}</div>
            </div>
          )
        ))}

      {totalItems > 10 && (
        <div className="d-flex align-items-center pt-2 pb-2 ps-2">
          {currentPage !== 1 && (
            <Button
              disabled={currentPage === 1}
              dataTestId="pagePreviousNavigateIcon"
              onClick={previousPage}
              className="d-flex align-items-center previous_icon page_button bg-transparent border-0 p-0"
            >
              {<RightArrowIcon />}
            </Button>
          )}
          <span data-testid="pagesCounts" className="font-fourteen fw-400 px-2">
            <span className="pe-1">{t('medicalClaims.page')}</span>{' '}
            {`${currentPage} / ${maxPages}`}
          </span>
          {currentPage !== maxPages && (
            <Button
              disabled={false}
              onClick={nextPage}
              dataTestId="pageNavigateIcon"
              className="d-flex align-items-center next_icon page_button bg-transparent border-0 p-0"
            >
              {<RightArrowIcon />}
            </Button>
          )}
        </div>
      )}

      {/* To show the internalServerError message */}
      <CommonErrorMessage message={errorMessage} />

      <DependentConsentModals consent={consent} />

      {/* disclaimer Message */}
      {!isAvMed() && (
        <div className="black-color pt-4">
          <Paragraph
            text={t('medicalClaims.disclaimer1')}
            variant="regular"
            className="discList"
            data-testid="disclaimerHeader"
          />
          {apiResponseSafeCheck(getPhone?.data) ? (
            <Paragraph
              text={
                <>
                  {t('medicalClaims.disclaimer21')}{' '}
                  <ContactInfo
                    phoneData={{
                      phone1800: getPhone?.data?.benefitPhone1800,
                      phoneLocal: getPhone?.data?.benefitPhoneLocal,
                    }}
                  />
                  {t('medicalClaims.disclaimer22')}
                </>
              }
              variant="regular"
              className="discList"
              data-testid="disclaimerHeader"
            />
          ) : (
            <Paragraph
              text={t('medicalClaims.disclaimer23')}
              variant="regular"
              className="discList"
              data-testid="disclaimerHeader"
            />
          )}
          <Paragraph
            text={t('medicalClaims.disclaimer3')}
            variant="regular"
            className="discList"
            data-testid="disclaimerHeader"
          />
          <Paragraph text={t('medicalClaims.disclaimer4')} variant="regular" />
          <Paragraph
            text={
              <>
                {t('medicalClaims.disclaimer5')}
                <Link
                  to={`mailto:${t('medicalClaims.disclaimer6')}`}
                  className="ws-nowrap text-decoration-underline px-1"
                  data-testid="customerMailId"
                >
                  {t('medicalClaims.disclaimer6')}
                </Link>
                {apiResponseSafeCheck(getPhone?.data) ? (
                  <>
                    {t('medicalClaims.disclaimer7')}{' '}
                    <ContactInfo
                      phoneData={{
                        phone1800: getPhone?.data?.benefitPhone1800,
                        phoneLocal: getPhone?.data?.benefitPhoneLocal,
                      }}
                      className="ps-1"
                    />
                  </>
                ) : null}
              </>
            }
            variant="regular"
            data-testid="disclaimer5"
            className="mb-5"
          />
        </div>
      )}
    </>
  );
};
export default MedicalClaims;

export const isValidDateFormat = (dateString: string) => {
  if (!dateString || dateString.length !== 10) return false;

  const dateParts = dateString.split('/');
  if (dateParts.length !== 3) return false;

  const month = parseInt(dateParts[0], 10);
  const day = parseInt(dateParts[1], 10);
  const year = parseInt(dateParts[2], 10);

  // Check if month is valid (1-12)
  if (month < 1 || month > 12) return false;

  // Check if day is valid (1-31, but we'll let Date constructor handle month-specific limits)
  if (day < 1 || day > 31) return false;

  // Create date and check if it matches the input (handles invalid dates like Feb 30)
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};
